import { type CrowdexUIMessagePart, INTERRUPTED_ERROR } from '@crowdex/agent-contracts';
import { basename } from '@/lib/desktop-paths';
import {
  type CodeStep,
  codeRunOperations,
  codeStep,
  isCodeCallPart,
  isCodeRunPart,
  stepImages,
} from './code-mode';
import { lineCount } from './text';
import { dynamicToolActivityKind, toolDetail } from './tool-identity';
import { testRunDuration, testRunSummary } from './turn-steps';
import type { DiffPart, DynamicToolPart, FilePart, TerminalPart } from './types';

/** One row of a turn's opened steps (design D12, mobile 10). */
export type StepRow =
  | { kind: 'reads'; key: string; paths: string[] }
  | { kind: 'search'; key: string; title: string; results: number | null }
  | {
      kind: 'edit';
      key: string;
      /** The file's name, and its path as the edit gave it (for Review). */
      path: string;
      fullPath: string;
      additions: number;
      deletions: number;
      excerpt: { sign: '-' | '+'; text: string }[];
    }
  | {
      kind: 'command';
      key: string;
      command: string;
      detail: string;
      state: 'running' | 'done' | 'failed';
      terminalId: string | null;
    }
  | { kind: 'thought'; key: string; text: string; running: boolean; durationMs: number | null }
  | { kind: 'text'; key: string; text: string }
  /** A code-mode cell (`crowdex_run`), named by what its code calls, its operations folded in. */
  | { kind: 'code'; key: string; part: DynamicToolPart; step: CodeStep; images: FilePart[] }
  | { kind: 'tool'; key: string; part: DynamicToolPart; terminal?: TerminalPart };

const EXCERPT_LINES = 4;

/**
 * The few lines that say what an edit did (design D11, D12): its removed lines, then its added
 * ones, without the lines both versions keep.
 */
export function diffExcerpt(
  oldText: string,
  newText: string,
  limit = 3,
): { sign: '-' | '+'; text: string }[] {
  const before = oldText.split('\n');
  const after = newText.split('\n');
  const kept = new Set(before.filter((line) => after.includes(line)));
  const removed = before.filter((line) => !kept.has(line) && line.trim() !== '');
  const added = after.filter((line) => !kept.has(line) && line.trim() !== '');
  if (removed.length > 0 && added.length > 0) {
    const removedShown = Math.max(
      1,
      Math.min(removed.length, limit - Math.min(added.length, limit - 1)),
    );
    return [
      ...removed.slice(0, removedShown).map((text) => ({ sign: '-' as const, text })),
      ...added.slice(0, limit - removedShown).map((text) => ({ sign: '+' as const, text })),
    ];
  }
  return [
    ...removed.map((text) => ({ sign: '-' as const, text })),
    ...added.map((text) => ({ sign: '+' as const, text })),
  ].slice(0, limit);
}

/** What a finished command printed, in a few words: "412 passed · 42 s", "Done in 18s". */
function commandDetail(terminal: TerminalPart['data'] | undefined, running: boolean): string {
  if (running) return 'Running';
  if (terminal === undefined) return 'Done';
  const summary = testRunSummary(terminal.output);
  if (summary !== null) {
    const counts = summary.match(/\d+ (?:passed|failed|passing|failing)/gi)?.join(', ') ?? summary;
    const duration = testRunDuration(terminal.output);
    return duration === null ? counts : `${counts} · ${duration}`;
  }
  if (terminal.exitCode !== null && terminal.exitCode !== 0) return `Exit ${terminal.exitCode}`;
  const last = terminal.output
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .at(-1);
  return last === undefined ? 'Done' : last.length > 48 ? `${last.slice(0, 47)}…` : last;
}

/** How long a thought took, as the runtime stamped it at its end (`crowdex.durationMs`). */
function thoughtDuration(metadata: unknown): number | null {
  const crowdex = (metadata as { crowdex?: { durationMs?: unknown } } | undefined)?.crowdex;
  return typeof crowdex?.durationMs === 'number' ? crowdex.durationMs : null;
}

/** How many results a search found, when its output says: a list, or `results`/`matches`. */
function resultCount(output: unknown): number | null {
  if (Array.isArray(output)) return output.length;
  if (typeof output !== 'object' || output === null) return null;
  const values = output as Record<string, unknown>;
  for (const key of ['results', 'matches', 'count', 'total']) {
    const value = values[key];
    if (typeof value === 'number') return value;
    if (Array.isArray(value)) return value.length;
  }
  return null;
}

/**
 * A turn's step parts as rows: reads in a row become one ("Read 4 files"), an edit carries its
 * diff, a command its result, a thought its whole text. A step the user must act on (an
 * interrupted tool) stays the tool's own card.
 */
export function stepRows(
  parts: readonly CrowdexUIMessagePart[],
  /** The whole message's parts, where the steps' images are. */
  messageParts: readonly CrowdexUIMessagePart[] = parts,
): StepRow[] {
  const operations = codeRunOperations(parts);
  const terminals = new Map<string, TerminalPart>();
  const diffs = new Map<string, DiffPart>();
  const tools = new Set<string>();
  for (const part of parts) {
    if (part.type === 'data-terminal') terminals.set(part.data.toolCallId, part);
    if (part.type === 'data-diff') diffs.set(part.data.toolCallId, part);
    if (part.type === 'dynamic-tool') tools.add(part.toolCallId);
  }
  const rows: StepRow[] = [];
  const editRow = (key: string, diff: DiffPart | undefined, fullPath: string): StepRow => ({
    kind: 'edit',
    key,
    path: basename(fullPath),
    fullPath,
    additions: diff === undefined ? 0 : lineCount(diff.data.newText),
    deletions: diff === undefined ? 0 : lineCount(diff.data.oldText),
    excerpt:
      diff === undefined
        ? []
        : diffExcerpt(diff.data.oldText ?? '', diff.data.newText, EXCERPT_LINES),
  });
  parts.forEach((part, index) => {
    const key = `step-${index}`;
    if (part.type === 'reasoning') {
      if (part.text.trim() === '' && part.state !== 'streaming') return;
      rows.push({
        kind: 'thought',
        key,
        text: part.text.trim(),
        running: part.state === 'streaming',
        durationMs: thoughtDuration(part.providerMetadata),
      });
      return;
    }
    if (part.type === 'text') {
      if (part.text.trim() !== '') rows.push({ kind: 'text', key, text: part.text });
      return;
    }
    if (part.type === 'data-diff') {
      if (!tools.has(part.data.toolCallId)) rows.push(editRow(key, part, part.data.path));
      return;
    }
    if (part.type === 'data-terminal') {
      if (tools.has(part.data.toolCallId)) return;
      const running = part.data.exitCode === null && part.data.signal === null;
      rows.push({
        kind: 'command',
        key,
        command: 'Command',
        detail: commandDetail(part.data, running),
        state: running ? 'running' : part.data.exitCode === 0 ? 'done' : 'failed',
        terminalId: part.data.terminalId,
      });
      return;
    }
    if (part.type !== 'dynamic-tool') return;
    const terminal = terminals.get(part.toolCallId);
    const diff = diffs.get(part.toolCallId);
    if (part.state === 'output-error' && part.errorText === INTERRUPTED_ERROR) {
      rows.push({ kind: 'tool', key, part, ...(terminal === undefined ? {} : { terminal }) });
      return;
    }
    // A cell's operations show in its row.
    if (operations.folded.has(part.toolCallId)) return;
    if (isCodeRunPart(part) || isCodeCallPart(part)) {
      rows.push({
        kind: 'code',
        key,
        part,
        step: codeStep(part, operations.byRun.get(part.toolCallId) ?? []),
        images: stepImages(messageParts, part.toolCallId),
      });
      return;
    }
    const kind = dynamicToolActivityKind(part);
    const detail = toolDetail(part, null);
    const running =
      part.state === 'input-streaming' ||
      part.state === 'input-available' ||
      part.state === 'approval-requested';
    if (diff !== undefined || kind === 'file-changes') {
      rows.push(editRow(key, diff, diff?.data.path ?? detail));
      return;
    }
    if (terminal !== undefined || kind === 'commands') {
      const failed =
        part.state === 'output-error' ||
        (terminal !== undefined && terminal.data.exitCode !== null && terminal.data.exitCode !== 0);
      rows.push({
        kind: 'command',
        key,
        command: detail,
        // A failed command never reads "Done": without its output, it says it failed.
        detail:
          part.state === 'approval-requested'
            ? 'Waits on you'
            : failed && terminal === undefined
              ? 'Failed'
              : commandDetail(terminal?.data, running),
        state: running ? 'running' : failed ? 'failed' : 'done',
        terminalId: terminal?.data.terminalId ?? null,
      });
      return;
    }
    if (kind === 'exploration') {
      const previous = rows.at(-1);
      const path = basename(detail);
      if (previous?.kind === 'reads') previous.paths.push(path);
      else rows.push({ kind: 'reads', key, paths: [path] });
      return;
    }
    if (kind === 'web') {
      rows.push({
        kind: 'search',
        key,
        title: part.title ?? detail,
        results: part.state === 'output-available' ? resultCount(part.output) : null,
      });
      return;
    }
    rows.push({ kind: 'tool', key, part, ...(terminal === undefined ? {} : { terminal }) });
  });
  return rows;
}
